import { Descriptions, Divider, Flex, Tooltip, Typography } from 'antd';
import { useIntl } from 'react-intl';
import style from './index.module.scss';
import { InfoCircleFilled } from '@ant-design/icons';
import ExtraComponent from '../ExtraComponent';
import AccionaButton from '../../../../../../../components/AccionaButton';
import PropTypes from 'prop-types';
import Modal from '../../../../../../../components/Modal';
import StructureTypeList from '../../utils/StructureTypeList';

const { wrapper, descriptions, footer } = style;

const ViewModal = ({ item, onClose, normative }) => {
  const intl = useIntl();

  const commonsProps = {
    colon: false,
    layout: 'vertical',
    column: 6,
    labelStyle: {
      color: '#000000',
      textTransform: 'uppercase',
    },
    className: descriptions,
    contentStyle: { marginBottom: '3em', fontSize: '15px', paddingRight: 16 },
  };

  const generalItems = [
    {
      key: 'docket_number',
      label: intl.formatMessage({ id: 'docket_number' }),
      children: item?.docket_number,
    },
    {
      key: 'plant',
      label: intl.formatMessage({ id: 'plant' }),
      children: item?.plant,
    },
    {
      key: 'recipe',
      label: intl.formatMessage({ id: 'recipe' }),
      children: item?.recipe,
    },
    {
      key: 'quantity',
      label: intl.formatMessage({ id: 'quantity' }),
      children: item?.delivered,
    },
    {
      key: 'elements',
      label: intl.formatMessage({ id: 'elements' }),
      children: item?.elements || '',
    },
  ];

  const batchItems = [
    {
      key: 'arriving_date',
      label: intl.formatMessage({ id: 'arriving_date' }),
      children: item?.arriving_date,
    },
    {
      key: 'structural_element',
      label:
        normative === 'coe' ? (
          <Flex align="center">
            {intl.formatMessage({ id: 'structural_element' })}
            <Divider type="vertical" />
            <Tooltip
              title={<StructureTypeList />}
              overlayInnerStyle={{ width: '40vw', padding: '2em 1em' }}
            >
              <InfoCircleFilled />
            </Tooltip>
          </Flex>
        ) : (
          intl.formatMessage({ id: 'structural_element' })
        ),
      children: item?.structural_element,
    },
    {
      key: 'control_type',
      label: intl.formatMessage({ id: 'control_type' }),
      children: item?.control_type,
    },
    {
      key: 'concrete_batch',
      label: intl.formatMessage({ id: 'concrete_batch' }),
      children: item?.concreteBatch ?? '--',
    },
    {
      key: 'batch_volume',
      label: intl.formatMessage({ id: 'batch_volume' }),
      children: item?.quantity,
    },
    {
      key: 'batch_compliance',
      label: (
        <Flex justify="center">
          {intl.formatMessage({ id: 'batch_compliance' })}
          <Divider type="vertical" />
          <Tooltip title={intl.formatMessage({ id: 'batch_compliance_info' })}>
            <InfoCircleFilled />
          </Tooltip>
        </Flex>
      ),
      children: item?.batchCompliance || '--',
    },
  ];

  const laboratoryItems = [
    {
      key: 'reference_number',
      label: intl.formatMessage({ id: 'reference_number' }),
      children: item?.laboratoryNoteData?.reference_number ?? '--',
    },
    {
      key: 'specimens_number',
      label: intl.formatMessage({ id: 'specimens_number' }),
      children: item?.specimens_number ?? '--',
    },
  ];

  const compressiveItems = [
    {
      key: 'compressive_strength_28_1',
      label: `28 ${intl.formatMessage({ id: 'days' })}`,
      children: item?.laboratoryNoteData?.compressive_strength_28_1 ?? '--',
    },
    {
      key: 'compressive_strength_28_2',
      label: `28 ${intl.formatMessage({ id: 'days' })}`,
      children: item?.laboratoryNoteData?.compressive_strength_28_2 ?? '--',
    },
    {
      key: 'compressive_strength_28_3',
      label: `28 ${intl.formatMessage({ id: 'days' })}`,
      children: item?.laboratoryNoteData?.compressive_strength_28_3 ?? '--',
    },
    {
      key: 'extra',
      label: 'extra',
      children:
        item?.laboratoryNoteData?.compressive_strength_3_1 ||
        item?.laboratoryNoteData?.compressive_strength_7_1 ||
        item?.laboratoryNoteData?.compressive_strength_60_1 ? (
          <ExtraComponent data={item?.rawData} />
        ) : (
          '--'
        ),
    },
  ];

  const renderDescriptions = (items) => (
    <Descriptions {...commonsProps}>
      {items.map(({ key, label, children }) => (
        <Descriptions.Item key={key} label={label}>
          {children}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );

  return (
    <Modal
      open={!!item}
      title={intl
        .formatMessage({
          id: 'pages.quality.normative.traceability.view.title',
        })
        .toUpperCase()}
      closable
      width="70vw"
      onClose={onClose}
      onCancel={onClose}
    >
      <div className={wrapper}>
        {renderDescriptions(generalItems)}
        {renderDescriptions(batchItems)}
        <Divider />
        <Typography.Paragraph>
          {intl
            .formatMessage({
              id: 'pages.quality.normative.traceability.view.laboratoryData',
            })
            .toUpperCase()}
        </Typography.Paragraph>
        {renderDescriptions(laboratoryItems)}
        {renderDescriptions(compressiveItems)}
        <div className={footer}>
          <AccionaButton
            type="default"
            onClick={onClose}
            msg={intl.formatMessage({ id: 'close' })}
          />
        </div>
      </div>
    </Modal>
  );
};

ViewModal.propTypes = {
  normative: PropTypes.oneOf(['coe', 'ehe']).isRequired,
  item: PropTypes.shape({
    docket_number: PropTypes.element,
    plant: PropTypes.string,
    recipe: PropTypes.string,
    delivered: PropTypes.element,
    elements: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    arriving_date: PropTypes.string,
    structural_element: PropTypes.string,
    control_type: PropTypes.string,
    concreteBatch: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    batchCompliance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    laboratoryNoteData: PropTypes.shape({
      reference_number: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      compressive_strength_28_1: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      compressive_strength_28_2: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      compressive_strength_28_3: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      compressive_strength_3_1: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      compressive_strength_7_1: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      compressive_strength_60_1: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    }),
    specimens_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rawData: PropTypes.any,
  }),
  onClose: PropTypes.func.isRequired,
};

ViewModal.defaultProps = {
  item: null,
};

export default ViewModal;
